import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  Truck,
  Store,
  ClipboardCheck,
  Flame,
  Gamepad2,
  Sparkles,
  Star,
  Leaf,
  Lamp,
  MapPin,
  ShieldCheck,
  Wallet,
  Clock,
  Plus,
  Minus,
} from "lucide-react";
import { audiences, gameCatalog } from "./audienceContent";
import LeadCapture from "../components/LeadCapture";
import "./MarketingExperience.css";

const roleIcons = {
  buyers: ShoppingBag,
  drivers: Truck,
  pickers: ClipboardCheck,
  sellers: Store,
};
const roleKeys = Object.keys(audiences);
const paths = {
  home: "/",
  propane: "/propane-exchange",
  rewards: "/rewards",
  buyers: "/buyers",
  drivers: "/drivers",
  pickers: "/pickers",
  sellers: "/sellers",
};
const descriptions = {
  home: "Shop local, choose your work, grow your business and discover KwK Play. Buyers, drivers, pickers and sellers, connected in one app.",
  propane:
    "Explore Propane Exchange with a step-by-step KwK Swap walkthrough: choose your tank service, review a local offer and follow your order.",
  rewards:
    "Meet Universal KwK Points and Seller Points. Learn how eligible earning offers, clear terms and redemption work as the program rolls out.",
};

function PageMeta({ page }) {
  useEffect(() => {
    const title = audiences[page]
      ? `For ${audiences[page].label}`
      : page === "propane"
        ? "Propane Exchange"
        : page === "rewards"
          ? "Points & Rewards"
          : "Your neighborhood. More rewarding.";
    document.title = `${title} | KwK`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        descriptions[page] || audiences[page]?.intro || descriptions.home,
      );
  }, [page]);
  return null;
}

export function CTA({ to = "/#join", role, children, secondary = false }) {
  return (
    <Link
      className={`mk-button ${secondary ? "mk-button-secondary" : ""}`}
      to={to}
      state={role ? { role } : undefined}
    >
      {children}
      <ArrowUpRight size={19} aria-hidden="true" />
    </Link>
  );
}

function SectionTitle({ eyebrow, title, children, light = false }) {
  return (
    <div className={`mk-section-title ${light ? "mk-light" : ""}`}>
      <span className="mk-eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

function RoleLinks() {
  return (
    <div className="mk-role-links">
      {roleKeys.map((key) => {
        const Icon = roleIcons[key];
        return (
          <Link key={key} to={paths[key]}>
            <Icon size={19} aria-hidden="true" />
            <span>{audiences[key].verb} with KwK</span>
            <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        );
      })}
    </div>
  );
}

function Phone({ title, children, label = "Illustrative app walkthrough" }) {
  return (
    <div className="mk-phone">
      <div className="mk-phone-top">
        <span>KwK</span>
        <span className="mk-phone-camera" />
        <span>9:41</span>
      </div>
      <div className="mk-phone-heading">
        <span className="mk-dot" />
        {title}
      </div>
      <div className="mk-phone-content">{children}</div>
      <div className="mk-phone-bottom">{label}</div>
    </div>
  );
}

function Applet({ name, image, Icon = Sparkles, onClick }) {
  const content = (
    <>
      <span>
        {image ? (
          <img src={image} alt="" width="42" height="42" />
        ) : (
          <Icon size={29} aria-hidden="true" />
        )}
      </span>
      <small>{name}</small>
    </>
  );
  return onClick ? (
    <button className="mk-applet" type="button" onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className="mk-applet">{content}</div>
  );
}

export function BuyerPhone() {
  const [view, setView] = useState("home");
  return (
    <Phone
      title={
        view === "home"
          ? "Your neighborhood, in reach"
          : view === "games"
            ? "KwK Play"
            : "Your next local moment"
      }
    >
      {view !== "home" && (
        <button
          type="button"
          className="mk-back"
          onClick={() => setView("home")}
        >
          <ChevronLeft size={14} />
          Back to launcher
        </button>
      )}
      {view === "home" ? (
        <>
          <div className="mk-phone-greeting">
            <small>MAKE ROOM FOR YOUR DAY</small>
            <strong>What’s next?</strong>
          </div>
          <div className="mk-phone-search">
            <Sparkles size={17} />
            Ask KwK: dinner for four
          </div>
          <div className="mk-applet-grid">
            <Applet
              name="Grocery"
              image="/applets/grocery.png"
              onClick={() => setView("shop")}
            />
            <Applet
              name="Propane"
              image="/applets/propane.png"
              onClick={() => setView("propane")}
            />
            <Applet
              name="Games"
              Icon={Gamepad2}
              onClick={() => setView("games")}
            />
            <Applet
              name="Rewards"
              Icon={Star}
              onClick={() => setView("rewards")}
            />
            <Applet
              name="Household"
              Icon={ShoppingBag}
              onClick={() => setView("household")}
            />
            <Applet
              name="KwK Pay"
              image="/applets/kwk_pay.png"
              onClick={() => setView("wallet")}
            />
          </div>
          <div className="mk-phone-note">
            <Check size={16} />
            Your applets. Your arrangement.
          </div>
        </>
      ) : view === "games" ? (
        <>
          <h3>Take a little play break.</h3>
          <div className="mk-mini-games">
            {gameCatalog.slice(0, 4).map((g) => (
              <Link key={g.id} to={`/play/${g.id}`}>
                <GameArt game={g} />
                <span>{g.name}</span>
              </Link>
            ))}
          </div>
          <Link className="mk-text-link" to="/play">
            Explore all games <ArrowRight size={16} />
          </Link>
        </>
      ) : (
        <>
          <div className="mk-phone-greeting">
            <strong>
              {
                {
                  shop: "Shop your local favorites.",
                  propane: "Keep the grill going.",
                  rewards: "Good things add up.",
                  household: "One household. One list.",
                  wallet: "Keep your money in view.",
                }[view]
              }
            </strong>
          </div>
          <p>
            {
              {
                shop: "Browse items, review the cart and choose available fulfillment.",
                propane:
                  "Choose an exchange or a new tank, then review a local offer.",
                rewards:
                  "Explore points, coupons and named-seller gift cards. Program availability applies.",
                household:
                  "Prepare a shared cart and coordinate everyday essentials.",
                wallet:
                  "Review balances, requests and payment activity in KwK Pay.",
              }[view]
            }
          </p>
          <CTA
            secondary
            to={
              view === "propane"
                ? "/propane-exchange"
                : view === "rewards"
                  ? "/rewards"
                  : "/buyers"
            }
          >
            See how it works
          </CTA>
        </>
      )}
    </Phone>
  );
}

function WorkPhone({ role }) {
  const [step, setStep] = useState(0);
  const content = {
    drivers: {
      title: "Your next move",
      badge: "EXAMPLE DELIVERY OFFER",
      heading: "The whole trip, up front.",
      rows: [
        ["Pay", "Shown before acceptance"],
        ["Distance", "Approach + delivery"],
        ["Time", "Estimated, not guaranteed"],
      ],
      action: "Explore the trip",
      next: "Your choice comes first.",
      detail:
        "Review the route, compare it with your preferences, and decide whether the job fits.",
    },
    pickers: {
      title: "Shopping with care",
      badge: "EXAMPLE PICKER WORKFLOW",
      heading: "The little details matter.",
      rows: [
        ["Milk", "Found · quantity checked"],
        ["Bread", "Replacement requested"],
        ["Apples", "Still to shop"],
      ],
      action: "See the replacement step",
      next: "Ask the person you’re shopping for.",
      detail:
        "When the preference is “ask,” propose the item and price. Keep the buyer’s decision attached to the order.",
    },
    sellers: {
      title: "Your store, connected",
      badge: "EXAMPLE SELLER WORKSPACE",
      heading: "Make today a good day.",
      rows: [
        ["Orders", "See what needs attention"],
        ["Inventory", "Keep availability current"],
        ["Rewards", "Create a reason to return"],
      ],
      action: "Explore a promotion",
      next: "A clear offer. A reason to return.",
      detail:
        "Choose the products, conditions and dates. Review the budget and terms before publishing a funded campaign.",
    },
  }[role];
  return (
    <Phone title={content.title}>
      <span className="mk-phone-tag">{content.badge}</span>
      <h3>{step ? content.next : content.heading}</h3>
      {step ? (
        <>
          <p>{content.detail}</p>
          <div className="mk-phone-success">
            <ShieldCheck size={35} />
            <strong>
              {role === "drivers"
                ? "Your decision."
                : role === "pickers"
                  ? "Their approval."
                  : "Your terms."}
            </strong>
          </div>
        </>
      ) : (
        <div className="mk-task-list">
          {content.rows.map(([a, b]) => (
            <div key={a}>
              <span className="mk-task-check">
                <Check size={13} />
              </span>
              <span>
                <strong>{a}</strong>
                <small>{b}</small>
              </span>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="mk-button mk-phone-action"
        onClick={() => setStep(step ? 0 : 1)}
      >
        {step ? "Back to example" : content.action}
        <ArrowRight size={16} />
      </button>
      <p className="mk-fine">
        Product illustration. Not a live offer, job or campaign.
      </p>
    </Phone>
  );
}

function Hero({ page }) {
  const home = page === "home",
    data = audiences[page];
  return (
    <section className={`mk-hero mk-tone-${home ? "orange" : data.color}`}>
      <div className="mk-container mk-hero-grid">
        <div className="mk-hero-copy">
          <span className="mk-eyebrow">
            {home ? "LOCAL LIFE, CONNECTED" : data.eyebrow}
          </span>
          <h1>
            {home ? (
              <>
                Your neighborhood.
                <br />
                <em>More rewarding.</em>
              </>
            ) : (
              data.title
            )}
          </h1>
          <p>
            {home
              ? "Shop the essentials. Find your next job. Grow your local business. And make time for a little play. Meet the app that brings your neighborhood together."
              : data.intro}
          </p>
          <div className="mk-actions">
            <CTA to={home ? "/buyers" : "/#join"} role={data?.role}>
              {home ? "Discover KwK" : data.cta}
            </CTA>
            <CTA secondary to={home ? "/play" : "#how-it-works"}>
              {home ? "Meet KwK Play" : "See how it works"}
            </CTA>
          </div>
          <p className="mk-fine">
            {home
              ? "One app. Four ways to belong. Services vary by area."
              : "Account eligibility and local availability apply."}
          </p>
        </div>
        <div className="mk-hero-art">
          <div className="mk-orbit mk-orbit-one" />
          <div className="mk-orbit mk-orbit-two" />
          {home || page === "buyers" ? (
            <BuyerPhone />
          ) : (
            <WorkPhone role={page} />
          )}
          <div className="mk-float mk-float-top">
            <Star size={20} />
            <span>
              More than the next order.<strong>A reason to come back.</strong>
            </span>
          </div>
          <div className="mk-float mk-float-bottom">
            <MapPin size={20} />
            <span>
              Real local connections.<strong>All in your corner.</strong>
            </span>
          </div>
        </div>
      </div>
      {home && (
        <div className="mk-container">
          <RoleLinks />
        </div>
      )}
    </section>
  );
}

function Ecosystem() {
  const [selected, setSelected] = useState("buyers");
  const story = {
    buyers: [
      "“I need dinner—and my evening back.”",
      "A buyer starts the order, chooses replacement preferences and stays connected along the way.",
    ],
    sellers: [
      "“Let’s make this a regular stop.”",
      "The store keeps products and hours current, fulfills the order and gives the customer a reason to return.",
    ],
    pickers: [
      "“I’ll find the right things.”",
      "A picker shops with care, checks the details and asks about replacements when approval is needed.",
    ],
    drivers: [
      "“This trip fits my day.”",
      "A driver reviews the offer, accepts the work and follows the delivery through to proof of completion.",
    ],
  };
  return (
    <section id="ecosystem" className="mk-section">
      <div className="mk-container">
        <SectionTitle
          eyebrow="FOUR ROLES. ONE CONNECTED EXPERIENCE."
          title="Good for you. Good for the people around you."
        >
          Not every order needs every role. When it does, the people doing the
          work should stay connected.
        </SectionTitle>
        <div className="mk-ecosystem">
          <div
            className="mk-role-chooser"
            aria-label="Explore a role in an order"
          >
            {roleKeys.map((key) => {
              const Icon = roleIcons[key];
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={selected === key}
                  onClick={() => setSelected(key)}
                  className={selected === key ? "is-selected" : ""}
                >
                  <Icon size={23} />
                  <span>{audiences[key].label}</span>
                  <ArrowRight size={18} />
                </button>
              );
            })}
          </div>
          <div
            className={`mk-role-story mk-tone-${audiences[selected].color}`}
            aria-live="polite"
          >
            <span className="mk-eyebrow">
              {audiences[selected].label.toUpperCase()} IN THE LOOP
            </span>
            <h3>{story[selected][0]}</h3>
            <p>{story[selected][1]}</p>
            <Link to={paths[selected]} className="mk-text-link">
              See what’s in it for {audiences[selected].label.toLowerCase()}{" "}
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function GameArt({ game }) {
  return (
    <div className={`mk-game-art mk-art-${game.color}`}>
      <img
        className="mk-native-cover"
        src={`/game-art/${game.id}.jpg`}
        alt={`${game.name} cover artwork`}
        width="768"
        height="480"
        loading="lazy"
      />
      <span className="mk-game-orbit" />
    </div>
  );
}

export function GamesSection({ full = false }) {
  return (
    <section className="mk-section mk-play-band" id="games">
      <div className="mk-container">
        <div className="mk-section-heading">
          <SectionTitle
            eyebrow="MEET KWK PLAY"
            title="Your next little obsession."
          >
            Five games. Match, remember, explore and chase your
            personal best. Download only the games you choose.
          </SectionTitle>
          {!full && (
            <CTA to="/play" secondary>
              Explore all games
            </CTA>
          )}
        </div>
        <div className={`mk-game-grid ${full ? "mk-game-grid-full" : ""}`}>
          {(full ? gameCatalog : gameCatalog.slice(0, 4)).map((g) => (
            <Link className="mk-game-card" to={`/play/${g.id}`} key={g.id}>
              <GameArt game={g} />
              <div>
                <span>{g.genre}</span>
                <h3>{g.name}</h3>
                <p>{g.detail}</p>
                <ArrowUpRight size={20} aria-hidden="true" />
              </div>
            </Link>
          ))}
        </div>
        <div className="mk-play-note">
          <ShieldCheck size={20} />
          <p>
            Play for the fun of it. Current games are private single-player
            experiences. Game scores and power-ups are not spendable points.
            Eligible sponsored rewards have separate terms.
          </p>
        </div>
      </div>
    </section>
  );
}

function RewardsPreview() {
  return (
    <section className="mk-section mk-reward-section" id="rewards">
      <div className="mk-container mk-split">
        <div>
          <span className="mk-eyebrow">INTRODUCING KWK POINTS</span>
          <h2>
            Good things
            <br />
            <em>add up.</em>
          </h2>
          <p>
            A great local offer should be easy to understand. The expanded KwK
            Points experience connects eligible earning opportunities with
            offers from participating sellers—with your balances and the terms
            in view.
          </p>
          <span className="mk-status">
            Program rollout · availability applies
          </span>
          <div className="mk-actions">
            <CTA to="/rewards">Meet your points</CTA>
          </div>
        </div>
        <div className="mk-reward-stack">
          <div className="mk-points-card">
            <Sparkles size={27} />
            <small>ACROSS PARTICIPATING SELLERS</small>
            <h3>Universal KwK Points</h3>
            <p>More places to discover an eligible offer.</p>
          </div>
          <div className="mk-points-card mk-seller-points">
            <Store size={27} />
            <small>WITH THE BUSINESS YOU LOVE</small>
            <h3>Seller Points</h3>
            <p>A reason to return to that particular seller.</p>
          </div>
          <p className="mk-fine">
            Points are not cash and have no fixed dollar value. Gift-card
            dollars and KwK Pay balances remain separate.
          </p>
        </div>
      </div>
    </section>
  );
}

function PropaneTeaser() {
  return (
    <section className="mk-section">
      <div className="mk-container mk-propane-teaser">
        <div>
          <span className="mk-eyebrow">PROPANE EXCHANGE</span>
          <h2>
            The grill is ready.
            <br />
            Is your tank?
          </h2>
          <p>
            Turn the last-minute propane run into a local order. Explore the KwK
            Swap experience, from choosing your tank service to following the
            handoff.
          </p>
          <CTA to="/propane-exchange">Try the screen walkthrough</CTA>
        </div>
        <TankArt />
        <span className="mk-teaser-stamp">
          One less errand.
          <br />
          One better weekend.
        </span>
      </div>
    </section>
  );
}

function TankArt() {
  return (
    <svg
      className="mk-tank"
      viewBox="0 0 250 300"
      role="img"
      aria-label="Illustration of a propane exchange cylinder"
    >
      <ellipse cx="125" cy="278" rx="86" ry="12" fill="#000" opacity=".08" />
      <rect
        x="86"
        y="18"
        width="78"
        height="61"
        rx="18"
        fill="#f4f2ec"
        stroke="#242821"
        strokeWidth="5"
      />
      <rect x="105" y="32" width="40" height="33" rx="9" fill="#e9b166" />
      <path d="M116 56V37h18v19" fill="#69735f" />
      <path
        d="M72 81q53-42 106 0l13 26v119q0 37-66 37t-66-37V107Z"
        fill="#faf8f1"
        stroke="#242821"
        strokeWidth="5"
      />
      <path d="M64 118h122v81H64Z" fill="#ff871d" />
      <text
        x="125"
        y="153"
        textAnchor="middle"
        fontSize="32"
        fontWeight="900"
        fill="#242821"
      >
        KwK
      </text>
      <text
        x="125"
        y="179"
        textAnchor="middle"
        fontSize="15"
        fontWeight="800"
        fill="#242821"
      >
        SWAP
      </text>
      <path
        d="M83 250v25h84v-25"
        fill="#e2e5dc"
        stroke="#242821"
        strokeWidth="5"
      />
      <path
        d="M79 105v-9m0 123v-15"
        stroke="#fff"
        strokeWidth="8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FAQs({ items }) {
  return (
    <section className="mk-section mk-faq">
      <div className="mk-container">
        <SectionTitle
          eyebrow="A FEW THINGS WORTH KNOWING"
          title="Good questions. Straight answers."
        />
        <div>
          {items.map(([q, a]) => (
            <details key={q}>
              <summary>
                {q}
                <Plus size={18} />
              </summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function RolePage({ page }) {
  const data = audiences[page];
  return (
    <>
      <Hero page={page} />
      <section className="mk-section" id="fair-pay">
        <div className="mk-container">
          <SectionTitle
            eyebrow={`THE KWK WAY TO ${data.verb.toUpperCase()}`}
            title={data.kicker}
          />
          <div className="mk-benefits">
            {data.benefits.map(([title, body], i) => (
              <article key={title}>
                <span className="mk-number">0{i + 1}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className={`mk-section mk-tone-${data.color}`} id="how-it-works">
        <div className="mk-container">
          <SectionTitle
            eyebrow="FROM FIRST TAP TO NEXT STEP"
            title={
              page === "buyers"
                ? "A day with fewer loose ends."
                : page === "drivers"
                  ? "Build a workday that fits."
                  : page === "pickers"
                    ? "A better shop, step by step."
                    : "From your shelves to their routine."
            }
          />
          <div className="mk-steps">
            {data.steps.map(([title, body], i) => (
              <article key={title}>
                <span>0{i + 1}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
          <div className="mk-applet-strip">
            {data.applets.map((a) => (
              <span key={a}>{a}</span>
            ))}
          </div>
        </div>
      </section>
      {page === "buyers" && (
        <>
          <RewardsPreview />
          <GamesSection />
        </>
      )}
      {page === "sellers" && (
        <section className="mk-section">
          <div className="mk-container mk-split">
            <div>
              <SectionTitle
                eyebrow="GIVE THEM A REASON TO RETURN"
                title="Don’t just get discovered. Get remembered."
              >
                Bring your offers into the places people already explore:
                shopping, circulars and eligible sponsored game experiences. The
                expanded points program adds another route back to your business
                as it rolls out.
              </SectionTitle>
              <div className="mk-actions">
                <CTA to="/rewards">Explore the points program</CTA>
                <CTA secondary to="/game-promotions">
                  Seller advertising desk
                </CTA>
              </div>
            </div>
            <div className="mk-business-card">
              <Store size={42} />
              <h3>
                Your terms.
                <br />
                Your budget.
                <br />
                Their next visit.
              </h3>
              <a className="mk-text-link" href="https://kwkpos.com">
                Open KwK POS <ArrowUpRight size={18} />
              </a>
              <p className="mk-fine">
                Existing seller account required. Advertising and rewards are
                separate commitments.
              </p>
            </div>
          </div>
        </section>
      )}
      <FAQs items={data.faq} />
      <section className="mk-section mk-role-close" id="requirements">
        <div className="mk-container">
          <span className="mk-eyebrow">YOUR NEXT CHAPTER IS LOCAL</span>
          <h2>
            {page === "buyers"
              ? "Make more room for your day."
              : page === "drivers"
                ? "Put your next move in your hands."
                : page === "pickers"
                  ? "Bring your good eye for shopping."
                  : "Let’s put your business in their day."}
          </h2>
          <CTA role={data.role}>{data.cta}</CTA>
          <p className="mk-fine">
            Tell us your area and role. We’ll help you find the appropriate next
            step.
          </p>
        </div>
      </section>
    </>
  );
}

const propaneSteps = [
  [
    "Start with your tank",
    "Choose Exchange if you have an eligible tank to return, or New tank if you need one. Confirm tank type and condition with the seller.",
  ],
  [
    "Find your local option",
    "Compare an available seller, item price and estimated timing. The real app uses your address and current inventory.",
  ],
  [
    "Review before you order",
    "Check quantity, fulfillment, charges and handoff instructions. Your final quote—not this example—sets the actual price.",
  ],
  [
    "Stay in the loop",
    "Follow order updates and contact the people handling your order. Delivery proof and required handoff checks stay with the order.",
  ],
  [
    "Keep the next cookout simple",
    "Return to your order for its receipt, help, or the next purchase. No automatic reorder is created by this walkthrough.",
  ],
];

function PropanePage() {
  const [step, setStep] = useState(0),
    [service, setService] = useState("Exchange"),
    [quantity, setQuantity] = useState(1);
  return (
    <>
      <section className="mk-propane-hero">
        <div className="mk-container mk-split">
          <div>
            <span className="mk-eyebrow">
              PROPANE EXCHANGE · MEET THE KWK SWAP EXPERIENCE
            </span>
            <h1>
              Keep the plans.
              <br />
              <em>Skip the propane run.</em>
            </h1>
            <p>
              An empty tank shouldn’t end a good afternoon. Discover local
              propane options, review the order and stay connected through the
              handoff.
            </p>
            <div className="mk-actions">
              <CTA to="#walkthrough">Explore KwK Swap</CTA>
              <CTA to="/#join" role="seller" secondary>
                I sell propane
              </CTA>
            </div>
            <p className="mk-fine">
              Service, tank eligibility, stock and pricing depend on your local
              seller.
            </p>
          </div>
          <TankArt />
        </div>
      </section>
      <section className="mk-section" id="walkthrough">
        <div className="mk-container">
          <SectionTitle
            eyebrow="TRY THE SCREEN PROCESS"
            title="From an empty tank to your next order."
          >
            An interactive reconstruction of the app’s propane flow—not
            screenshots, live stock, a quote or a real order. “KwK Swap” is the
            exchange experience; the current app’s entry is labeled Propane.
          </SectionTitle>
          <div className="mk-walkthrough">
            <div className="mk-walkthrough-steps">
              {propaneSteps.map(([title, body], i) => (
                <button
                  key={title}
                  type="button"
                  aria-current={step === i ? "step" : undefined}
                  onClick={() => setStep(i)}
                  className={step === i ? "is-selected" : ""}
                >
                  <span>0{i + 1}</span>
                  <div>
                    <h3>{title}</h3>
                    {step === i && <p>{body}</p>}
                  </div>
                </button>
              ))}
            </div>
            <Phone
              title="KwK Swap · Propane Exchange"
              label="Interactive demo · no order is placed"
            >
              <div aria-live="polite">
                <span className="mk-phone-tag">STEP {step + 1} OF 5</span>
                <h3>{propaneSteps[step][0]}</h3>
                {step === 0 ? (
                  <>
                    <div className="mk-choice">
                      {["Exchange", "New tank"].map((v) => (
                        <button
                          type="button"
                          key={v}
                          aria-pressed={service === v}
                          onClick={() => setService(v)}
                        >
                          {v === service && <Check size={16} />} {v}
                        </button>
                      ))}
                    </div>
                    <TankArt />
                    <p className="mk-fine">
                      {service === "Exchange"
                        ? "Have an eligible empty tank ready for the handoff."
                        : "Choose a new tank if you don’t have one to exchange."}
                    </p>
                  </>
                ) : step === 1 ? (
                  <div className="mk-example-store">
                    <Store size={34} />
                    <strong>Example local propane seller</strong>
                    <p>{service} · stock checked in the app</p>
                    <span className="mk-phone-note">
                      <Clock size={15} />
                      Timing confirmed for your address
                    </span>
                    <p className="mk-fine">
                      Demonstration seller. Not a live business listing.
                    </p>
                  </div>
                ) : step === 2 ? (
                  <>
                    <div className="mk-quantity">
                      <span>{service}</span>
                      <button
                        aria-label="Decrease tanks"
                        disabled={quantity === 1}
                        onClick={() => setQuantity((q) => q - 1)}
                      >
                        <Minus size={16} />
                      </button>
                      <output aria-label="Tank quantity">{quantity}</output>
                      <button
                        aria-label="Increase tanks"
                        disabled={quantity === 4}
                        onClick={() => setQuantity((q) => q + 1)}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <dl className="mk-quote">
                      <div>
                        <dt>Tank service</dt>
                        <dd>Seller’s current price</dd>
                      </div>
                      <div>
                        <dt>Delivery / pickup</dt>
                        <dd>Available options</dd>
                      </div>
                      <div>
                        <dt>Total</dt>
                        <dd>Review in the app</dd>
                      </div>
                    </dl>
                    <p>
                      Your order’s final quote appears in the app before you
                      pay.
                    </p>
                  </>
                ) : step === 3 ? (
                  <>
                    <div className="mk-demo-route">
                      <MapPin size={27} />
                      <div />
                      <Truck size={29} />
                      <div />
                      <ShoppingBag size={25} />
                    </div>
                    <div className="mk-task-list">
                      {[
                        "Seller prepares the order",
                        "Assigned driver follows the route",
                        "Handoff and order record",
                      ].map((t) => (
                        <div key={t}>
                          <Check size={16} />
                          {t}
                        </div>
                      ))}
                    </div>
                    <p className="mk-fine">
                      Illustrated progression, not live tracking.
                    </p>
                  </>
                ) : (
                  <div className="mk-phone-success">
                    <Check size={42} />
                    <strong>That’s the process.</strong>
                    <p>
                      No purchase was made. Open KwK to check your address,
                      seller options and final quote.
                    </p>
                    <CTA role="buyer">Ask about local availability</CTA>
                  </div>
                )}
              </div>
              <div className="mk-demo-controls">
                <button
                  type="button"
                  aria-label="Previous demo step"
                  disabled={!step}
                  onClick={() => setStep((s) => s - 1)}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => setStep((s) => (s === 4 ? 0 : s + 1))}
                >
                  {step === 4 ? "Start again" : "Next screen"}
                  <ChevronRight size={18} />
                </button>
              </div>
            </Phone>
          </div>
        </div>
      </section>
      <FAQs
        items={[
          [
            "What happens to my empty tank?",
            "For an exchange, follow the seller’s eligibility and handoff instructions. Confirm the tank’s type and condition before placing the order; an ineligible tank may require a different service.",
          ],
          [
            "Is there a fixed price or delivery time?",
            "No. The seller’s price, your location, stock and fulfillment options determine the quote. Review the actual order before paying.",
          ],
          [
            "Can my store participate?",
            "Use the seller inquiry option to discuss your propane business, location and fulfillment capabilities. Required approvals and handling requirements still apply.",
          ],
        ]}
      />
    </>
  );
}

function RewardsPage() {
  const [mode, setMode] = useState("both");
  const split =
    mode === "both"
      ? ["300", "200"]
      : mode === "seller"
        ? ["500", "0"]
        : ["0", "500"];
  return (
    <>
      <section className="mk-rewards-hero">
        <div className="mk-container">
          <span className="mk-eyebrow">KWK POINTS · PROGRAM PREVIEW</span>
          <h1>
            Make local life
            <br />
            <em>a little more rewarding.</em>
          </h1>
          <p>
            Choose an eligible earning offer. Know its rules. Put confirmed
            points toward an offer you actually want. That’s the idea behind the
            expanded KwK Points experience.
          </p>
          <span className="mk-status">
            Rollout in progress · not a promise of live earning
          </span>
        </div>
      </section>
      <section className="mk-section">
        <div className="mk-container mk-split">
          <div>
            <SectionTitle
              eyebrow="TWO BALANCES. CLEAR PURPOSES."
              title="Go exploring. Stay loyal."
            >
              Universal KwK Points are for eligible offers across participating
              sellers. Seller Points belong to that seller’s program. Neither is
              cash, and neither has a fixed dollar conversion.
            </SectionTitle>
            <div className="mk-actions">
              <CTA to="/play">Explore the games</CTA>
              <CTA secondary to="/sellers">
                Create reasons to return
              </CTA>
            </div>
          </div>
          <div className="mk-points-explorer">
            <span className="mk-eyebrow">
              ILLUSTRATIVE REDEMPTION · NOT A LIVE OFFER
            </span>
            <h3>See the split before you confirm.</h3>
            <p>
              Example: an offer requiring 500 eligible points. Choose an example
              point type to see how the review changes.
            </p>
            <div className="mk-choice">
              {[
                ["both", "Both types"],
                ["seller", "Seller only"],
                ["universal", "Universal only"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={mode === v}
                  onClick={() => setMode(v)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="mk-point-totals" aria-live="polite">
              <div>
                <strong>{split[0]}</strong>
                <span>Seller Points</span>
              </div>
              <Plus size={20} />
              <div>
                <strong>{split[1]}</strong>
                <span>Universal Points</span>
              </div>
            </div>
            <p className="mk-fine">
              Actual offers decide accepted points, required amounts, products,
              locations, channels and deadlines. Eligible Seller Points are used
              first when both are accepted.
            </p>
          </div>
        </div>
      </section>
      <section className="mk-section mk-tone-purple">
        <div className="mk-container">
          <SectionTitle
            eyebrow="A REWARD SHOULD NEVER BE A GUESS"
            title="Know what earns. Know what it gets."
          >
            The expanded program is being prepared for participating accounts
            and sellers. Existing funded game rewards retain their own terms.
          </SectionTitle>
          <div className="mk-steps">
            {[
              [
                "Choose the offer",
                "Review sponsor, qualifying activity, limits and dates before enrolling. No blanket “every action earns” promise.",
              ],
              [
                "Wait for confirmation",
                "Eligible game objectives and purchases are verified. Pending or held points are not spendable; returned purchases can reverse awards.",
              ],
              [
                "Review and redeem",
                "See the exact point split, restrictions and reservation deadline. Coupons, purchased gift cards and KwK Pay remain distinct.",
              ],
            ].map(([a, b], i) => (
              <article key={a}>
                <span>0{i + 1}</span>
                <h3>{a}</h3>
                <p>{b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <GamesSection />
      <FAQs
        items={[
          [
            "Does playing always earn points?",
            "No. Free play, score and gameplay tools are separate from funded rewards. Earning requires an eligible, active program and server verification. Rewards Lab balances are test-only and cannot be spent in the live marketplace.",
          ],
          [
            "Can I cash out points?",
            "No. Points have no fixed dollar value and are not a cash balance. Review a participating seller’s eligible offers for redemption options.",
          ],
          [
            "Are gift cards the same as points?",
            "No. Purchased gift cards are denominated in dollars and belong to a named seller. Promotional gift cards have separate restrictions. KwK Pay is another distinct balance.",
          ],
          [
            "Is the expanded program available now?",
            "Rollout and financial activation are still gated in the current release records. Check the app for what is actually enabled for your account. This page explains the direction, not an offer of currently available earnings.",
          ],
        ]}
      />
    </>
  );
}

export default function MarketingExperience({ page = "home" }) {
  const { pathname } = useLocation();
  return (
    <main className="mk-site" id="main-content">
      <PageMeta page={page} />
      {page === "home" ? (
        <>
          <Hero page="home" />
          <Ecosystem />
          <RewardsPreview />
          <GamesSection />
          <PropaneTeaser />
          <section className="mk-section" id="launcher">
            <div className="mk-container mk-launcher-story">
              <SectionTitle
                eyebrow="BUILT AROUND YOU"
                title="Not another endless feed. Your own little neighborhood."
              >
                Arrange your applets. Keep your favorite widgets close. Switch
                roles when your day changes. The KwK launcher gives shopping,
                work, rewards and play a home that feels like yours.
              </SectionTitle>
              <div className="mk-actions">
                <CTA to="/buyers">Make it your KwK</CTA>
                <CTA secondary to="/pickers">
                  Meet the people who pick
                </CTA>
              </div>
            </div>
          </section>
          <div id="service-area" className="mk-availability">
            <MapPin size={22} />
            <p>
              <strong>Local really means local.</strong> Stores, services and
              work opportunities depend on your address. Tell us where you are;
              we won’t pretend a demo proves coverage.
            </p>
            <CTA role="buyer">Ask about your area</CTA>
          </div>
          <LeadCapture key={pathname} />
        </>
      ) : page === "propane" ? (
        <PropanePage />
      ) : page === "rewards" ? (
        <RewardsPage />
      ) : (
        <RolePage key={page} page={page} />
      )}
    </main>
  );
}
